import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot, Tooltip } from 'radix-ui';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'soft' | 'danger' | 'danger-ghost' | 'link';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm' | 'icon-xs';
  loading?: boolean;
  disabledReason?: string;
  asChild?: boolean;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    asChild,
    disabled,
    disabledReason,
    type,
    className,
    children,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button';
  const button = (
    <Comp
      ref={ref}
      type={asChild ? undefined : type || 'button'}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      className={cn('action', `action-${variant}`, `action-${size}`, className)}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Loader2 size={14} className="action-spinner animate-spin" aria-hidden="true" />}
          {children}
        </>
      )}
    </Comp>
  );
  const reason = loading
    ? 'Please wait while this action finishes.'
    : disabledReason || (disabled ? props.title : undefined);
  if (asChild || !(disabled || loading) || !reason) return button;
  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span className="disabled-action-hint" tabIndex={0} aria-label={reason}>
            {button}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content className="disabled-action-tooltip" sideOffset={6} collisionPadding={12}>
            {reason}
            <Tooltip.Arrow className="disabled-action-tooltip-arrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
});
